import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContextClientInsurerCollectionType } from '../../../../context/billing.context';
import { useClientInsurersByIdsLazy } from '../../../../hooks/client-insurer-hook';
import { useAbortableTrigger } from '../../../../hooks/use-abortable-trigger';
import {
  BillingType,
  ClientType,
  GetContractsV2Query,
  InsurerType,
  PartnershipType,
  SubmissionsPaginatedType,
  SubmissionStatusEnum,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetBillingTypesQuery,
  useGetContractIdsLazyQuery,
  useGetContractsV2LazyQuery,
  useGetPartnershipsQuery,
  useGetSubmissionsLazyQuery,
} from '../../../../types';
import { selectSorterFunction } from '../../../../utils/bills-utils';
import { SubmissionManagementGridFilterModelType } from '../../bill-grid-components/bill-management-types';

export interface SubmissionQueriesType {
  ciData: ContextClientInsurerCollectionType;
  contractsData: GetContractsV2Query['getContractsV2'];
  clientsData: ClientType[];
  insurersData: Omit<InsurerType, 'eligibilityRef' | 'memberIdRequired'>[];
  partnershipsData: PartnershipType[];
  billingTypesData: BillingType[];
  submissionsData: SubmissionsPaginatedType;
  isLoading: boolean;
  hasError: boolean;
  searchClientInsurerIds: (ids: Set<number>) => void;
}

const emptySubmissionsData: SubmissionsPaginatedType = {
  items: [],
  page: 1,
  pages: 1,
  size: 0,
};

export const useSubmissionQueries = (
  filterParams: SubmissionManagementGridFilterModelType,
  paginationModel: { page: number; pageSize: number },
): SubmissionQueriesType => {
  const { data: ciData, searchClientInsurerIds } = useClientInsurersByIdsLazy();

  const [
    getContracts,
    { data: contractsData, loading: contractsLoading, error: contractsError },
  ] = useGetContractsV2LazyQuery();

  const [getContractIds, { error: contractIdsError }] =
    useGetContractIdsLazyQuery();

  const {
    data: clientsData,
    error: clientsError,
    loading: clientsLoading,
  } = useGetAllClientsQuery();

  const {
    data: insurersData,
    error: insurersError,
    loading: insurersLoading,
  } = useGetAllInsurersQuery();

  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({
      variables: { type: 'billing' },
    });

  const [
    getSubmissions,
    { loading: submissionsLoading, error: submissionsError },
  ] = useGetSubmissionsLazyQuery();

  const [submissionsResult, setSubmissionsResult] =
    useState<SubmissionsPaginatedType>(emptySubmissionsData);

  const {
    data: billingTypesData,
    loading: billingTypesLoading,
    error: billingTypesError,
  } = useGetBillingTypesQuery();

  const stableFilterParams = useMemo(
    () => ({
      submissionIds: filterParams.submissionIds,
      billIds: filterParams.billIds,
      userIds: filterParams.userIds,
      transactionIds: filterParams.transactionIds,
      netsuiteIds: filterParams.netsuiteIds,
      creditMemoIds: filterParams.creditMemoIds,
      submissionStatuses: filterParams.submissionStatuses,
      submissionStartDate: filterParams.submissionStartDate,
      submissionEndDate: filterParams.submissionEndDate,
      clientIds: filterParams.clientIds,
      insurerIds: filterParams.insurerIds,
      partnershipIds: filterParams.partnershipIds,
      billingTypes: filterParams.billingTypes,
      source: filterParams.source,
      sourceIds: filterParams.sourceIds,
      sourceStartDate: filterParams.sourceStartDate,
      sourceEndDate: filterParams.sourceEndDate,
    }),
    [
      filterParams.submissionIds,
      filterParams.billIds,
      filterParams.userIds,
      filterParams.transactionIds,
      filterParams.netsuiteIds,
      filterParams.creditMemoIds,
      filterParams.submissionStatuses,
      filterParams.submissionStartDate,
      filterParams.submissionEndDate,
      filterParams.clientIds,
      filterParams.insurerIds,
      filterParams.partnershipIds,
      filterParams.billingTypes,
      filterParams.source,
      filterParams.sourceIds,
      filterParams.sourceStartDate,
      filterParams.sourceEndDate,
    ],
  );

  const stablePaginationModel = useMemo(
    () => ({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    }),
    [paginationModel.page, paginationModel.pageSize],
  );

  const fetchSubmissions = useCallback(
    async (signal: AbortSignal): Promise<void> => {
      let contractIds: number[] = [];

      try {
        if (
          stableFilterParams.clientIds.length > 0 ||
          stableFilterParams.insurerIds.length > 0 ||
          stableFilterParams.billingTypes.length > 0 ||
          stableFilterParams.partnershipIds.length > 0
        ) {
          const { data } = await getContractIds({
            variables: {
              clientIds: stableFilterParams.clientIds,
              insurerIds: stableFilterParams.insurerIds,
              billingTypes: stableFilterParams.billingTypes.map(type =>
                type.toLowerCase(),
              ),
              partnershipIds: stableFilterParams.partnershipIds,
              // Include voided contracts so filtering by client/insurer/partnership
              // still surfaces submissions on voided contracts (record-level void is
              // filtered separately and stays excluded).
              includeVoided: true,
            },
            context: { fetchOptions: { signal } },
          });
          if (signal.aborted) return;
          if (data?.getContractIds?.length) {
            contractIds = data.getContractIds;
          } else {
            setSubmissionsResult(emptySubmissionsData);
            return;
          }
        }
        const result = await getSubmissions({
          variables: {
            page: stablePaginationModel.page + 1,
            size: stablePaginationModel.pageSize,
            submissionIds: stableFilterParams.submissionIds,
            billIds: stableFilterParams.billIds,
            contractIds,
            creditMemoIds: stableFilterParams.creditMemoIds,
            netsuiteIds: stableFilterParams.netsuiteIds,
            submissionStatuses:
              stableFilterParams.submissionStatuses as SubmissionStatusEnum[],
            submissionStartDate: stableFilterParams.submissionStartDate,
            submissionEndDate: stableFilterParams.submissionEndDate,
            source: stableFilterParams.source,
            sourceIds: stableFilterParams.sourceIds,
            sourceStartDate: stableFilterParams.sourceStartDate,
            sourceEndDate: stableFilterParams.sourceEndDate,
            transactionIds: stableFilterParams.transactionIds,
            userIds: stableFilterParams.userIds,
          },
          context: { fetchOptions: { signal } },
        });
        if (signal.aborted) return;
        setSubmissionsResult(
          result.data?.getSubmissions ?? emptySubmissionsData,
        );
      } catch {
        if (!signal.aborted) {
          setSubmissionsResult(emptySubmissionsData);
        }
      }
    },
    [stableFilterParams, stablePaginationModel, getContractIds, getSubmissions],
  );

  const refetch = useAbortableTrigger(fetchSubmissions);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Fetch contract data to enrich submission rows.
  // Only fetch contracts for the currently returned submissions for performance.
  useEffect(() => {
    const contractIds = submissionsResult.items
      .map(submission => submission.contractId)
      .filter((id): id is number => id != null);
    if (contractIds.length > 0) {
      getContracts({
        variables: {
          size: stablePaginationModel.pageSize,
          page: 1,
          contractIds,
          requireBillingTypeOverrideMatch: false,
          // Include voided contracts so submissions on a voided contract still resolve
          // their client/insurer for display instead of showing "Loading..." forever.
          includeVoided: true,
        },
      });
    }
  }, [submissionsResult, stablePaginationModel.pageSize, getContracts]);

  const hasError =
    !!submissionsError ||
    !!clientsError ||
    !!insurersError ||
    !!contractsError ||
    !!contractIdsError ||
    !!billingTypesError;

  const initialDataLoading =
    clientsLoading ||
    insurersLoading ||
    partnershipsLoading ||
    billingTypesLoading;

  const isLoading =
    submissionsLoading || contractsLoading || initialDataLoading;

  return useMemo(
    () => ({
      ciData,
      contractsData: contractsData?.getContractsV2 ?? {
        items: [],
        page: 1,
        pages: 1,
        size: 0,
      },
      clientsData: [...(clientsData?.getAllClients ?? [])].sort((a, b) =>
        selectSorterFunction(a.name, b.name),
      ),
      insurersData: [...(insurersData?.getAllInsurers ?? [])].sort((a, b) =>
        selectSorterFunction(a.name, b.name),
      ),
      partnershipsData: [...(partnershipsData?.getPartnerships ?? [])].sort(
        (a, b) => selectSorterFunction(a.name, b.name),
      ),
      billingTypesData: [...(billingTypesData?.getBillingTypes ?? [])].sort(
        (a, b) => selectSorterFunction(a.value, b.value),
      ),
      submissionsData: submissionsResult,
      isLoading,
      hasError,
      searchClientInsurerIds,
    }),
    [
      ciData,
      contractsData?.getContractsV2,
      clientsData?.getAllClients,
      insurersData?.getAllInsurers,
      partnershipsData?.getPartnerships,
      billingTypesData?.getBillingTypes,
      submissionsResult,
      isLoading,
      hasError,
      searchClientInsurerIds,
    ],
  );
};
